import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, ArrowDown } from "lucide-react";
import { Link } from "react-router-dom";
import heroBg from "@/assets/hero-bg.jpg";

const slides = [
  {
    id: 1,
    description:
      "Escritório especializado em direito tributário, com atuação técnica, ética e foco total em resultados.",
  },
  {
    id: 2,
    description:
      "Liderado pela advogada tributarista Luiza Mourad Sotsek, oferecemos soluções inteligentes para planejamento tributário, recuperação de créditos e defesa fiscal em São Paulo e todo o Brasil.",
  },
  {
    id: 3,
    description:
      "Um escritório que valoriza ética e relações sólidas. Atendimento 100% online, com rigor técnico para garantir que você pague apenas o devido.",
  },
];

export const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [direction, setDirection] = useState(0);

  const nextSlide = useCallback(() => {
    setDirection(1);
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, []);

  const prevSlide = useCallback(() => {
    setDirection(-1);
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, []);

  useEffect(() => {
    const timer = setInterval(nextSlide, 8000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 100 : -100,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 100 : -100,
      opacity: 0,
    }),
  };

  return (
    <section className="relative h-screen w-full overflow-hidden">
      {/* Background Image with Parallax */}
      <motion.div
        className="absolute inset-0"
        initial={{ scale: 1.1 }}
        animate={{ scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
      >
        <img
          src={heroBg}
          alt="Sotsek Advocacia - Escritório de advocacia tributária em São Paulo"
          className="w-full h-full object-cover"
          loading="eager"
          fetchPriority="high"
          decoding="async"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/40 via-background/20 to-background/80" />
      </motion.div>

      {/* Content */}
      <div className="relative h-full container mx-auto px-6 lg:px-12">
        <div className="h-full flex flex-col pt-24 lg:pt-32 pb-24 lg:pb-32">
          {/* Main Title Section - Upper portion */}
          <div className="flex flex-col items-center justify-center text-center pt-8 lg:pt-12">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-4xl sm:text-5xl lg:text-7xl xl:text-8xl font-normal tracking-wide text-white leading-tight mb-6 text-center"
            >
              Prepare-se para a
              <br />
              Reforma Tributária
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <Link
                to="/noticias/preparese-para-a-reforma-tributaria-548663"
                className="inline-flex items-center gap-2 text-lg lg:text-xl text-white hover:text-white/80 transition-colors duration-300 group"
              >
                <span className="relative">
                  Saiba Mais
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-white transition-all duration-300 group-hover:w-full" />
                </span>
              </Link>
            </motion.div>
          </div>

          {/* Spacer - invisible container to separate title from carousel */}
          <div className="flex-1 min-h-[120px] lg:min-h-[180px]" />

          {/* Bottom Section - Description Carousel */}
          <div className="pb-16">
            <div className="flex items-center justify-center gap-4 lg:gap-8">
              {/* Description */}
              <div className="max-w-xl text-center">
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.p
                    key={currentSlide}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                    className="text-sm lg:text-base text-white/90 leading-relaxed font-body"
                  >
                    {slides[currentSlide].description}
                  </motion.p>
                </AnimatePresence>
              </div>

              {/* Right Arrow */}
              <motion.button
                onClick={nextSlide}
                whileHover={{ scale: 1.1, x: 3 }}
                whileTap={{ scale: 0.95 }}
                className="w-10 h-10 lg:w-12 lg:h-12 rounded-full border border-primary/50 flex items-center justify-center text-primary hover:border-primary hover:text-primary transition-colors flex-shrink-0"
                aria-label="Próximo slide"
              >
                <ChevronRight className="w-4 h-4 lg:w-5 lg:h-5" aria-hidden="true" />
              </motion.button>
            </div>

            {/* Slide Indicators */}
            <div className="flex justify-center gap-3 mt-6" role="tablist" aria-label="Slides do carousel">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => {
                    setDirection(index > currentSlide ? 1 : -1);
                    setCurrentSlide(index);
                  }}
                  className="relative w-6 h-6 flex items-center justify-center"
                  role="tab"
                  aria-selected={index === currentSlide}
                  aria-label={`Ir para slide ${index + 1}`}
                >
                  <span
                    className={`block rounded-full transition-all duration-300 ${
                      index === currentSlide
                        ? "w-8 h-2 bg-primary"
                        : "w-2 h-2 bg-primary/40 hover:bg-primary/60"
                    }`}
                    aria-hidden="true"
                  />
                </button>
              ))}
            </div>
          </div>

          {/* Scroll Indicator */}
          <motion.a
            href="#areas"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="absolute bottom-8 right-8 hidden lg:flex flex-col items-center gap-2 text-white/50 hover:text-white transition-colors"
          >
            <span className="text-xs font-medium tracking-widest uppercase">Scroll</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ArrowDown className="w-4 h-4" />
            </motion.div>
          </motion.a>
        </div>
      </div>
    </section>
  );
};
